import React, { useState, useEffect, useCallback } from 'react';
import { X, Building2, Loader2, CheckCircle, XCircle, Image, Upload, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/ui/Button';
import { ImageCropper } from '../../components/ImageCropper';

interface Company {
  id: string;
  name: string | null;
  company_email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  zip?: string | null;
  address?: string | null;
  address2?: string | null;
  contact_fn: string | null;
  contact_ln: string | null;
  is_active: boolean;
  setup_complete: boolean;
  slogan?: string | null;
  ein?: string | null;
  logo_url?: string | null;
}

interface CompanyEditModalProps {
  company: Company;
  onClose: () => void;
  onSave: () => void;
}

export default function CompanyEditModal({ company, onClose, onSave }: CompanyEditModalProps) {
  const [formData, setFormData] = useState<Company>(company);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    setFormData(company);
  }, [company]);

  // Get current user ID for storage path
  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    getUserId();
  }, []);

  const handleChange = (field: keyof Company, value: string | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(false);
  };

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setError(null);
    setOriginalFile(file);
    
    // Create preview URL for cropping
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file: File) => {
    if (!userId) {
      setError('Please wait for authentication to complete.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
      const fileName = `company-logo-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/company-logos/${fileName}`;

      // Upload to Supabase Storage - avatars bucket
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        setError(`Failed to upload image: ${uploadError.message}`);
        return;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (urlData?.publicUrl) {
        setFormData((prev) => ({ ...prev, logo_url: urlData.publicUrl }));
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    setShowCropper(false);
    
    // Convert blob URL to File
    const response = await fetch(croppedImageUrl);
    const blob = await response.blob();
    const file = new File([blob], originalFile?.name || 'logo.png', {
      type: blob.type || 'image/png',
    });

    // Upload the cropped image
    await uploadFile(file);
    
    // Clean up
    URL.revokeObjectURL(croppedImageUrl);
    setImageToCrop(null);
    setOriginalFile(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    setOriginalFile(null);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const removeLogo = () => {
    setFormData((prev) => ({ ...prev, logo_url: null }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          name: formData.name || null,
          company_email: formData.company_email || null,
          phone: formData.phone || null,
          city: formData.city || null,
          state: formData.state || null,
          zip: formData.zip ?? null,
          address: formData.address ?? null,
          address2: formData.address2 ?? null,
          contact_fn: formData.contact_fn || null,
          contact_ln: formData.contact_ln || null,
          is_active: formData.is_active,
          setup_complete: formData.setup_complete,
          slogan: formData.slogan ?? null,
          ein: formData.ein ?? null,
          logo_url: formData.logo_url || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', company.id);

      if (updateError) {
        setError(updateError.message);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        onSave();
        onClose();
      }, 500);
    } catch (err) {
      console.error('[CompanyEditModal] Save error:', err);
      setError(err instanceof Error ? err.message : 'save_failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <header className="bg-[#363232] border-b border-[#504A4A] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-rollops-accent" />
            <div>
              <h2 className="text-xl font-bold text-[#EBFFEE]">Edit Company</h2>
              <p className="text-sm text-[#A9A3A3] font-mono">ID: {company.id.slice(0, 8)}...</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#A9A3A3] hover:text-[#EBFFEE] p-2 hover:bg-[#504A4A] rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-red-950/20 border border-red-900 rounded-lg px-4 py-3 text-text-primary text-sm">
              Error: {error}
            </div>
          )}

          {/* Success Display */}
          {success && (
            <div className="mb-4 bg-green-950/20 border border-green-900 rounded-lg px-4 py-3 text-text-primary text-sm flex items-center gap-2">
              <CheckCircle size={16} />
              Company updated successfully
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Logo Upload */}
            <section className="bg-[#1A1818] rounded-lg p-4 border border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4">Company Logo</h3>
              <div className="space-y-4">
                {formData.logo_url ? (
                  <div className="relative inline-block">
                    <img
                      src={formData.logo_url}
                      alt="Company logo"
                      className="w-32 h-32 rounded-lg object-cover border-2 border-[#504A4A]"
                    />
                    <div className="absolute top-0 right-0 flex gap-2">
                      <label
                        htmlFor="logo-upload-edit"
                        className="p-2 bg-[#9B1421] hover:bg-[#7A0F1A] rounded-lg cursor-pointer transition-colors"
                        title="Edit logo"
                      >
                        <Upload size={16} className="text-white" />
                        <input
                          id="logo-upload-edit"
                          type="file"
                          accept="image/*"
                          onChange={handleFileInputChange}
                          className="hidden"
                          disabled={uploading || loading}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="p-2 bg-[#504A4A] hover:bg-[#5A5454] rounded-lg transition-colors"
                        title="Remove logo"
                        disabled={uploading || loading}
                      >
                        <Trash2 size={16} className="text-[#EBFFEE]" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDragActive(false);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      dragActive
                        ? 'border-[#9B1421] bg-[#9B1421]/10'
                        : 'border-[#504A4A] bg-[#221F1F] hover:border-[#9B1421]/50'
                    }`}
                  >
                    <Image size={48} className="text-[#A9A3A3] mx-auto mb-4" />
                    <p className="text-[#EBFFEE] mb-2 font-medium">Drag & drop logo here</p>
                    <p className="text-sm text-[#A9A3A3] mb-4">or</p>
                    <label
                      htmlFor="logo-upload"
                      className="inline-block px-4 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white rounded-lg cursor-pointer transition-colors"
                    >
                      Choose File
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        className="hidden"
                        disabled={uploading || loading}
                      />
                    </label>
                    {uploading && (
                      <p className="text-sm text-[#A9A3A3] mt-4">Uploading...</p>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Basic Info */}
            <section className="bg-[#1A1818] rounded-lg p-4 border border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Company Name</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value || null)}
                    className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                    disabled={loading}
                    placeholder="Enter company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Company Email</label>
                  <input
                    type="email"
                    value={formData.company_email || ''}
                    onChange={(e) => handleChange('company_email', e.target.value || null)}
                    className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                    disabled={loading}
                    placeholder="company@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Phone</label>
                  <input
                    type="text"
                    value={formData.phone || ''}
                    onChange={(e) => handleChange('phone', e.target.value || null)}
                    className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                    disabled={loading}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Slogan</label>
                    <input
                      type="text"
                      value={formData.slogan ?? ''}
                      onChange={(e) => handleChange('slogan', e.target.value || null)}
                      className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                      disabled={loading}
                      placeholder="Company slogan"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">EIN</label>
                    <input
                      type="text"
                      value={formData.ein ?? ''}
                      onChange={(e) => handleChange('ein', e.target.value || null)}
                      className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                      disabled={loading}
                      placeholder="12-3456789"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Contact Info */}
            <section className="bg-[#1A1818] rounded-lg p-4 border border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4">Contact Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">First Name</label>
                  <input
                    type="text"
                    value={formData.contact_fn || ''}
                    onChange={(e) => handleChange('contact_fn', e.target.value || null)}
                    className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                    disabled={loading}
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.contact_ln || ''}
                    onChange={(e) => handleChange('contact_ln', e.target.value || null)}
                    className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                    disabled={loading}
                    placeholder="Last name"
                  />
                </div>
              </div>
            </section>

            {/* Address */}
            <section className="bg-[#1A1818] rounded-lg p-4 border border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4">Address</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Street Address</label>
                  <input
                    type="text"
                    value={formData.address ?? ''}
                    onChange={(e) => handleChange('address', e.target.value || null)}
                    className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                    disabled={loading}
                    placeholder="123 Main St"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Address Line 2</label>
                  <input
                    type="text"
                    value={formData.address2 ?? ''}
                    onChange={(e) => handleChange('address2', e.target.value || null)}
                    className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                    disabled={loading}
                    placeholder="Suite, Unit, etc."
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">City</label>
                    <input
                      type="text"
                      value={formData.city || ''}
                      onChange={(e) => handleChange('city', e.target.value || null)}
                      className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                      disabled={loading}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">State</label>
                    <input
                      type="text"
                      value={formData.state || ''}
                      onChange={(e) => handleChange('state', e.target.value || null)}
                      className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                      disabled={loading}
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">ZIP Code</label>
                    <input
                      type="text"
                      value={formData.zip ?? ''}
                      onChange={(e) => handleChange('zip', e.target.value || null)}
                      className="w-full bg-[#221F1F] border border-[#504A4A] rounded-lg px-4 py-2 text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                      disabled={loading}
                      placeholder="12345"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Status Flags */}
            <section className="bg-[#1A1818] rounded-lg p-4 border border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4">Status</h3>
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    className="w-5 h-5 border-2 border-[#504A4A] bg-[#221F1F] text-rollops-accent rounded focus:ring-2 focus:ring-[#9B1421] focus:ring-offset-2 focus:ring-offset-[#1A1818] cursor-pointer"
                    disabled={loading}
                  />
                  <div className="flex items-center gap-2">
                    {formData.is_active ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className="text-sm text-[#EBFFEE] font-medium">Active</span>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.setup_complete}
                    onChange={(e) => handleChange('setup_complete', e.target.checked)}
                    className="w-5 h-5 border-2 border-[#504A4A] bg-[#221F1F] text-rollops-accent rounded focus:ring-2 focus:ring-[#9B1421] focus:ring-offset-2 focus:ring-offset-[#1A1818] cursor-pointer"
                    disabled={loading}
                  />
                  <div className="flex items-center gap-2">
                    {formData.setup_complete ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-orange-500" />
                    )}
                    <span className="text-sm text-[#EBFFEE] font-medium">Setup Complete</span>
                  </div>
                </label>
              </div>
            </section>
          </div>
        </main>

        {/* Footer Actions */}
        <footer className="border-t border-[#504A4A] px-6 py-4 bg-[#1A1818] flex items-center justify-end gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={loading}
            className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
          >
            Cancel
          </Button>
          <Button
            variant="red"
            size="sm"
            onClick={handleSave}
            disabled={loading}
            className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </footer>
      </div>

      {/* Image Cropper Modal */}
      {showCropper && imageToCrop && (
        <ImageCropper
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
          cropShape="rect"
          minZoom={1}
          maxZoom={3}
        />
      )}
    </div>
  );
}

